import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { CopyIcon, InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { getCicDisplayValue, getReasonsForFee } from 'utils/cicReason';

import {
  getActiveCicFeeReasonsAction,
  getActiveCicLoanTermReasonsAction,
} from 'store/system/actions';

import {
  CANCEL_BUTTON_LABEL,
  CIC_FEE_REASONS_DESCRIPTION,
  CIC_FIELD_REASONS_DESCRIPTION,
  CONFIRM_BUTTON_LABEL,
  COPY_FIRST_ROW_LABEL,
  FEE_CHANGES_HEADING,
  LOAD_REASONS_ERROR_MESSAGE,
  NO_ACTIVE_REASONS_MESSAGE,
  NO_ITEMS,
  RECALCULATED_FIELDS_PREFIX,
  RECALCULATED_FIELDS_SUFFIX,
  SENDING_UNCHANGED_MESSAGE,
  FEE_REASON_DISCLOSURE_MESSAGE,
} from './CicFieldReasonsForm.constants';
import {
  copyButtonStyles,
  errorLineStyles,
  feeSectionStyles,
  footerButtonsStyles,
  footerStyles,
  formStyles,
  infoIconStyles,
  infoLineStyles,
  loaderStyles,
  rowsContainerStyles,
} from './CicFieldReasonsForm.styles';
import { CicFieldReasonsFormTestIds } from './CicFieldReasonsForm.testIds';
import {
  buildCicFeeReasons,
  buildCicReasons,
  copyFirstRowToAll,
  getCompletedFeeRowsCount,
  getCompletedRowsCount,
  getFeeRowValue,
  getInitialFeeRowValues,
  getInitialRowValues,
  getProgressLabel,
  getRecalculatedFieldKey,
  getRowValue,
} from './CicFieldReasonsForm.utils';
import CicFeeReasonRow from './components/CicFeeReasonRow';
import CicFieldReasonRow from './components/CicFieldReasonRow';

const CicFieldReasonsForm = ({
  changes,
  recalculatedFields = NO_ITEMS,
  saveMessage = '',
  previousCicReasons = NO_ITEMS,
  feeChanges = NO_ITEMS,
  previousCicFeeReasons = NO_ITEMS,
  onConfirm,
  onCancel,
}) => {
  const dispatch = useDispatch();

  const [reasons, setReasons] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(true);
  const [rowValues, setRowValues] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  const [feeReasons, setFeeReasons] = useState([]);
  const [feeRowValues, setFeeRowValues] = useState({});
  const [touchedFees, setTouchedFees] = useState({});

  const completedCount = useMemo(
    () =>
      getCompletedRowsCount(changes, rowValues) +
      getCompletedFeeRowsCount(feeChanges, feeRowValues),
    [changes, rowValues, feeChanges, feeRowValues],
  );

  const hasFieldChanges = changes.length > 0;
  const hasFeeChanges = feeChanges.length > 0;
  const totalCount = changes.length + feeChanges.length;
  const hasReasons = reasons.length > 0;
  const isConfirmDisabled = isLoadingReasons || isLoadFailed || completedCount !== totalCount;
  const firstRowValue = getRowValue(rowValues, changes[0]?.field);
  const isCopyHidden = changes.length < 2 || !hasReasons;
  const isCopyDisabled = !firstRowValue.cicReasonId && !firstRowValue.comment.trim();

  const reasonsByFeeType = useMemo(
    () =>
      Object.fromEntries(
        feeChanges.map(({ feeTypeId }) => [feeTypeId, getReasonsForFee(feeReasons, feeTypeId)]),
      ),
    [feeChanges, feeReasons],
  );

  const onReasonChange = useCallback((field, cicReasonId) => {
    setRowValues(prev => ({ ...prev, [field]: { ...getRowValue(prev, field), cicReasonId } }));
  }, []);

  const onCommentChange = useCallback((field, comment) => {
    setRowValues(prev => ({ ...prev, [field]: { ...getRowValue(prev, field), comment } }));
  }, []);

  const onFieldBlur = useCallback(field => {
    setTouchedFields(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const onCopyFirstRow = useCallback(() => {
    setRowValues(prev => copyFirstRowToAll(changes, prev));
  }, [changes]);

  const onFeeReasonChange = useCallback((feeTypeId, cicReasonId) => {
    setFeeRowValues(prev => ({
      ...prev,
      [feeTypeId]: { ...getFeeRowValue(prev, feeTypeId), cicReasonId },
    }));
  }, []);

  const onFeeNoteChange = useCallback((feeTypeId, note) => {
    setFeeRowValues(prev => ({
      ...prev,
      [feeTypeId]: { ...getFeeRowValue(prev, feeTypeId), note },
    }));
  }, []);

  const onFeeCureChange = useCallback((feeTypeId, isCure) => {
    setFeeRowValues(prev => ({
      ...prev,
      [feeTypeId]: { ...getFeeRowValue(prev, feeTypeId), isCure },
    }));
  }, []);

  const onFeeBlur = useCallback(feeTypeId => {
    setTouchedFees(prev => (prev[feeTypeId] ? prev : { ...prev, [feeTypeId]: true }));
  }, []);

  const onConfirmClick = useCallback(
    () =>
      onConfirm({
        cicReasons: buildCicReasons(changes, rowValues),
        cicFeeReasons: buildCicFeeReasons(feeChanges, feeRowValues),
      }),
    [changes, rowValues, feeChanges, feeRowValues, onConfirm],
  );

  useEffect(() => {
    Promise.all([
      hasFieldChanges ? dispatch(getActiveCicLoanTermReasonsAction()) : NO_ITEMS,
      hasFeeChanges ? dispatch(getActiveCicFeeReasonsAction()) : NO_ITEMS,
    ])
      .then(([activeReasons, activeFeeReasons]) => {
        setReasons(activeReasons);
        setRowValues(getInitialRowValues(changes, previousCicReasons, activeReasons));
        setFeeReasons(activeFeeReasons);
        setFeeRowValues(
          getInitialFeeRowValues(feeChanges, previousCicFeeReasons, activeFeeReasons),
        );
      })
      .catch(() => setIsLoadFailed(true))
      .finally(() => setIsLoadingReasons(false));
  }, [
    dispatch,
    changes,
    previousCicReasons,
    feeChanges,
    previousCicFeeReasons,
    hasFieldChanges,
    hasFeeChanges,
  ]);

  return (
    <Box data-testid={CicFieldReasonsFormTestIds.FORM} sx={formStyles}>
      <Typography color="text.secondary" variant="regular14">
        {hasFieldChanges ? CIC_FIELD_REASONS_DESCRIPTION : CIC_FEE_REASONS_DESCRIPTION}
      </Typography>

      {!!saveMessage && (
        <Box sx={errorLineStyles}>
          <Typography
            color="error.main"
            data-testid={CicFieldReasonsFormTestIds.SAVE_MESSAGE_LABEL}
            variant="regular14"
          >
            {saveMessage}
          </Typography>
        </Box>
      )}

      {isLoadingReasons && (
        <Box sx={loaderStyles}>
          <CircularProgress data-testid={CicFieldReasonsFormTestIds.LOADING_SPINNER} size={32} />
        </Box>
      )}

      {isLoadFailed && (
        <Box sx={errorLineStyles}>
          <Typography
            color="error.main"
            data-testid={CicFieldReasonsFormTestIds.LOAD_ERROR_LABEL}
            variant="regular14"
          >
            {LOAD_REASONS_ERROR_MESSAGE}
          </Typography>
        </Box>
      )}

      {!isLoadingReasons && !isLoadFailed && hasFieldChanges && !hasReasons && (
        <Box sx={errorLineStyles}>
          <Typography
            color="error.main"
            data-testid={CicFieldReasonsFormTestIds.NO_REASONS_LABEL}
            variant="regular14"
          >
            {NO_ACTIVE_REASONS_MESSAGE}
          </Typography>
        </Box>
      )}

      {!isLoadingReasons && hasFieldChanges && (
        <Box sx={rowsContainerStyles}>
          {changes.map(change => (
            <CicFieldReasonRow
              change={change}
              isDisabled={!hasReasons}
              isTouched={!!touchedFields[change.field]}
              key={change.field}
              reasons={reasons}
              value={getRowValue(rowValues, change.field)}
              onBlur={onFieldBlur}
              onCommentChange={onCommentChange}
              onReasonChange={onReasonChange}
            />
          ))}
        </Box>
      )}

      {!isLoadingReasons && !isCopyHidden && (
        <Button
          data-testid={CicFieldReasonsFormTestIds.COPY_FIRST_ROW_BUTTON}
          disabled={isCopyDisabled}
          startIcon={<CopyIcon size={IconSize[16]} />}
          sx={copyButtonStyles}
          variant="text"
          onClick={onCopyFirstRow}
        >
          {COPY_FIRST_ROW_LABEL}
        </Button>
      )}

      {!isLoadingReasons && !isLoadFailed && hasFeeChanges && (
        <Box data-testid={CicFieldReasonsFormTestIds.FEE_SECTION} sx={feeSectionStyles}>
          {hasFieldChanges && (
            <>
              <Typography variant="bold14">{FEE_CHANGES_HEADING}</Typography>
              <Typography color="text.secondary" variant="regular14">
                {CIC_FEE_REASONS_DESCRIPTION}
              </Typography>
            </>
          )}

          <Box sx={rowsContainerStyles}>
            {feeChanges.map(feeChange => (
              <CicFeeReasonRow
                feeChange={feeChange}
                isTouched={!!touchedFees[feeChange.feeTypeId]}
                key={feeChange.feeTypeId}
                reasons={reasonsByFeeType[feeChange.feeTypeId]}
                value={getFeeRowValue(feeRowValues, feeChange.feeTypeId)}
                onBlur={onFeeBlur}
                onCureChange={onFeeCureChange}
                onNoteChange={onFeeNoteChange}
                onReasonChange={onFeeReasonChange}
              />
            ))}
          </Box>
        </Box>
      )}

      {!!recalculatedFields.length && (
        <Box sx={infoLineStyles}>
          <InfoIcon size={IconSize[16]} style={infoIconStyles} />
          <Typography
            color="text.secondary"
            data-testid={CicFieldReasonsFormTestIds.RECALCULATED_FIELDS_LABEL}
            variant="regular14"
          >
            {`${RECALCULATED_FIELDS_PREFIX} `}
            {recalculatedFields.map(({ label, oldDisplay, newDisplay }, index) => (
              <React.Fragment key={getRecalculatedFieldKey({ label, oldDisplay, newDisplay })}>
                {index > 0 && ', '}
                <Typography component="span" variant="bold14">
                  {label}
                </Typography>
                {` ${getCicDisplayValue(oldDisplay)} → ${getCicDisplayValue(newDisplay)}`}
              </React.Fragment>
            ))}
            {`. ${RECALCULATED_FIELDS_SUFFIX}`}
          </Typography>
        </Box>
      )}

      <Box sx={infoLineStyles}>
        <InfoIcon size={IconSize[16]} style={infoIconStyles} />
        <Typography color="text.secondary" variant="regular14">
          {hasFeeChanges ? FEE_REASON_DISCLOSURE_MESSAGE : SENDING_UNCHANGED_MESSAGE}
        </Typography>
      </Box>

      <Box sx={footerStyles}>
        <Typography
          color="text.secondary"
          data-testid={CicFieldReasonsFormTestIds.PROGRESS_LABEL}
          variant="regular12"
        >
          {getProgressLabel({ completedCount, totalCount, hasFeeChanges })}
        </Typography>

        <Box sx={footerButtonsStyles}>
          <Button
            data-testid={CicFieldReasonsFormTestIds.CANCEL_BUTTON}
            size="large"
            variant="line"
            onClick={onCancel}
          >
            {CANCEL_BUTTON_LABEL}
          </Button>

          <Button
            data-testid={CicFieldReasonsFormTestIds.CONFIRM_BUTTON}
            disabled={isConfirmDisabled}
            size="large"
            onClick={onConfirmClick}
          >
            {CONFIRM_BUTTON_LABEL}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

CicFieldReasonsForm.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  feeChanges: PropTypes.arrayOf(
    PropTypes.shape({
      feeTypeId: PropTypes.string.isRequired,
      currentReasonId: PropTypes.string,
      currentNote: PropTypes.string,
    }),
  ),
  previousCicFeeReasons: PropTypes.arrayOf(
    PropTypes.shape({
      feeTypeId: PropTypes.string,
      cicReasonId: PropTypes.string,
      note: PropTypes.string,
      isCure: PropTypes.bool,
    }),
  ),
  previousCicReasons: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      cicReasonId: PropTypes.string,
      comment: PropTypes.string,
    }),
  ),
  recalculatedFields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      oldDisplay: PropTypes.string,
      newDisplay: PropTypes.string,
    }),
  ),
  saveMessage: PropTypes.string,
};

export default CicFieldReasonsForm;
